import mix from 'polished/lib/color/mix';
import styled, { css, CSSProperties } from 'styled-components';
import { FormikTouched } from 'formik';
import { transparentize } from 'polished';

import StyledGlobal, { resetButtonStylingCSS } from '@tmh-website/styles/global';
import StyledTypo from '@tmh-website/styles/typo';
import { Color } from '@tmh-website/shared/colors';

import { FormElementThemeColor, formElementThemes } from './FormElements.theme';

export interface HasFormElementThemeColor {
  $themeColor: FormElementThemeColor;
}

export const checkmarkSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%FILL%"><path d="M9.936 16.804l-.122-.003-.05-.006-.065-.01-.095-.024-.116-.044-.083-.042-.077-.048-.074-.056-.09-.087-3.9-4.2a1 1 0 0 1 1.374-1.447l.09.087 3.208 3.453 7.367-7.093a1 1 0 0 1 1.473 1.348l-.085.092-8.114 7.813a.998.998 0 0 1-.302.193l-.081.03-.11.026-.148.018z" /></svg>`;

interface InputLabelProps extends HasFormElementThemeColor {
  $fieldIsFilled: boolean;
  $hasElementLeft?: boolean;
  $centeredText?: boolean;
  $labelFontStyle?: 'normal' | 'italic' | 'oblique';
  /**
   * Determines the size of the input field.
   *
   * @default 'md'
   */
  $size?: 'md' | 'lg';
}

const LabelSmallCss = css<InputLabelProps>`
  top: ${props => (props.$size === 'lg' ? '10px' : '3px')};
  font-size: 10px;
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => transparentize(0.6, props.theme.color[formElementThemes[props.$themeColor].text])} !important;
`;

export const InputLabel = styled.label<InputLabelProps>`
  ${StyledTypo.uiTextCss};
  position: absolute;
  top: ${props => (props.$size === 'lg' ? '22px' : '10px')};
  left: ${props => (props.$hasElementLeft ? `${44 + 6}px` : props.theme.formElementsPaddingX)};
  z-index: 2;
  font-style: ${props => props.$labelFontStyle};
  color: ${props => transparentize(0.6, props.theme.color[formElementThemes[props.$themeColor].text])};
  white-space: nowrap;
  pointer-events: none;
  transition: all ${props => props.theme.transitionTiming};
  ${props =>
    props.$centeredText &&
    css`
      left: 50%;
      transform: translateX(-50%);
    `};

  ${props => props.$fieldIsFilled && LabelSmallCss};
`;

export const labelCss = css<HasFormElementThemeColor>`
  display: inline-block;
  margin: 0 auto 12px;
  font-size: 16px;
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.color[formElementThemes[props.$themeColor].text]};
`;

export interface LabelProps extends HasFormElementThemeColor {
  /**
   * Determines whether the label should be visible only for the screen readers
   * (hidden for the "normal" (sighted) Users).
   */
  $srOnly?: boolean;
}

export const Label = styled.label<LabelProps>`
  ${labelCss};
  ${props => props.$srOnly && StyledGlobal.srOnlyCss};
`;

export interface InputProps extends HasFormElementThemeColor {
  /**
   * Optional override for the default cursor.
   *
   * @default 'revert'
   */
  $cursor?: CSSProperties['cursor'];

  $smallPaddingLeft?: boolean;
  $smallPaddingRight?: boolean;
  $smallLabel?: boolean;
  $centeredText?: boolean;
  $themeColor: FormElementThemeColor;
  $hasPrefix?: boolean;
  $withoutLabel?: boolean;
  /**
   * Determines the size of the input field.
   *
   * @default 'md'
   */
  $size?: 'md' | 'lg';
  ref?: any;
}

export const Input = styled.input<InputProps>`
  ${StyledTypo.uiTextCss};
  display: block;
  width: 100%;
  min-height: ${props => (props.$size === 'lg' ? '67px' : '44px')};
  padding: ${props => props.theme.formElementsPadding};
  font-size: 16px;
  color: ${props => props.theme.color[formElementThemes[props.$themeColor].text]};
  text-align: left;
  background: transparent;
  border: none;
  outline: none;
  cursor: ${({ $cursor }) => $cursor || 'revert'};

  // Adjustments for iOS
  &::-webkit-date-and-time-value {
    margin-top: 3px;
    text-align: left;
  }

  ${props =>
    props.$centeredText &&
    css`
      text-align: center;
    `};

  &::placeholder {
    color: transparent;
    ${props =>
      props.$smallLabel &&
      css`
        color: ${transparentize(0.8, props.theme.color[formElementThemes[props.$themeColor].text])} !important;
      `};
  }

  &:focus::placeholder {
    color: ${props => transparentize(0.8, props.theme.color[formElementThemes[props.$themeColor].text])};
  }

  &:-webkit-autofill,
  &[data-com-onepassword-filled],
  &[data-autocompleted] {
    color: ${props => props.theme.color[formElementThemes[props.$themeColor].text]};
    -webkit-text-fill-color: ${props => props.theme.color[formElementThemes[props.$themeColor].text]};
    box-shadow: inset 800px 0 0
      ${props =>
        mix(
          0.93,
          props.theme.color[formElementThemes[props.$themeColor].background.normal],
          props.theme.color[formElementThemes[props.$themeColor].checked.normal]
        )};
  }

  ${props =>
    props.$smallPaddingRight &&
    css`
      padding-right: 6px !important;
    `}

  ${props =>
    props.$smallPaddingLeft &&
    css`
      padding-left: 6px !important;
    `}

  ${props =>
    props.$hasPrefix &&
    css`
      padding-left: 0;
    `}

  ${props =>
    props.$withoutLabel &&
    css`
      padding-top: 5px;
    `}

  ${props =>
    (props.disabled || props.readOnly) &&
    css`
      cursor: not-allowed;
      color: ${transparentize(0.6, props.theme.color[formElementThemes[props.$themeColor].text])};
    `}
`;

// A smaller version of the Input. Usually used as other field in group fields

export const InputSmall = styled.input<HasFormElementThemeColor>`
  ${StyledTypo.uiTextCss};
  display: block;
  width: 100%;
  padding: 4px 16px;
  color: ${props => props.theme.color[formElementThemes[props.$themeColor].text]};
  background: transparent;
  border: none;
  outline: none;

  &::placeholder {
    color: ${props => transparentize(0.8, props.theme.color[formElementThemes[props.$themeColor].text])};
  }

  ${props =>
    (props.disabled || props.readOnly) &&
    css`
      color: ${transparentize(0.6, props.theme.color[formElementThemes[props.$themeColor].text])};
    `};
`;

export const ClearButton = styled.button<{ $isVisible: boolean }>`
  ${resetButtonStylingCSS};
  width: 24px;
  height: 24px;
  margin: 14px 10px 0 0;
  opacity: 0.6;
  transition: opacity ${props => props.theme.transitionTiming};

  ${props =>
    !props.$isVisible &&
    css`
      opacity: 0 !important;
    `};

  &:hover {
    opacity: 0.4;
  }

  &:active {
    opacity: 0.2;
  }
`;

export const ExtraElement = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;

  button,
  svg {
    display: block;
  }
`;

export interface ContainerProps extends HasFormElementThemeColor {
  $hasError?: boolean | '' | FormikTouched<any> | FormikTouched<any>[];
  $isDisabled?: boolean;
  $isReadOnly?: boolean;
  $disableSmallLabelOnFocus?: boolean;
  $size?: 'md' | 'lg';
  ref?: any;
}

export const containerCss = css<ContainerProps>`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  background-color: ${props => props.theme.color[formElementThemes[props.$themeColor].background.normal]};
  border: 2px solid ${props => props.theme.color[formElementThemes[props.$themeColor].border.normal]};
  border-radius: ${props => props.theme.borderRadius.large};
  transition: border-color ${props => props.theme.transitionTiming};

  ${props =>
    (!props.$isDisabled || !props.$isReadOnly) &&
    css`
      &:hover {
        border: 2px solid ${props.theme.color[formElementThemes[props.$themeColor].border.hover]};

        ${InputLabel} {
          color: ${props.theme.color[formElementThemes[props.$themeColor].text]};
        }
      }

      &:focus-within {
        border-color: ${props.theme.color[formElementThemes[props.$themeColor].checked.normal]};

        ${!props.$disableSmallLabelOnFocus &&
        css`
          ${InputLabel} {
            // This does not work with the css component
            // \${LabelSmallCss};
            top: ${props.$size === 'lg' ? '10px' : '3px'};
            font-size: 10px;
            font-weight: ${props => props.theme.fontWeight.semibold};
            color: ${transparentize(0.6, props.theme.color[formElementThemes[props.$themeColor].text])} !important;
          }
        `}
      }
    `}

  ${props =>
    (props.$isDisabled || props.$isReadOnly) &&
    css`
      background-color: ${props.theme.color[formElementThemes[props.$themeColor].background.disabled]};
      border-color: ${props.theme.color[formElementThemes[props.$themeColor].border.disabled]};
      cursor: not-allowed;

      ${InputLabel} {
        pointer-events: none;
      }
    `}

  ${props =>
    props.$hasError &&
    css`
      border-color: ${props => props.theme.color[Color.Red_500_Danger]};
    `}
`;

export const Container = styled.div<ContainerProps>`
  ${containerCss}
`;

const messageCss = css`
  margin: ${props => props.theme.formElementsPadding};
  margin-top: 3px;
  margin-bottom: 0;
  font-size: 12px;
  font-weight: ${props => props.theme.fontWeight.semibold};
`;

export const HelpText = styled.p<Pick<ContainerProps, '$themeColor'>>`
  ${messageCss};
  color: ${props => transparentize(0.6, props.theme.color[formElementThemes[props.$themeColor].text])};
`;

export const ErrorMessage = styled.p`
  ${messageCss};
  color: ${props => props.theme.color[Color.Red_500_Danger]};
`;

/**
 * Group fields
 */

export const Group = styled.div<{ bottomRow?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
  ${props =>
    props.bottomRow &&
    css`
      margin-top: 12px;
    `}
`;

export const GroupOtherContainer = styled.div`
  display: flex;
`;

export const GroupOtherLabel = styled.div`
  margin-right: 8px;
`;

/**
 * Skeleton
 */
export const FormElementSkeleton = styled.div<HasFormElementThemeColor>`
  ${props => StyledGlobal.skeletonLoadingCss(formElementThemes[props.$themeColor].surface.normal)}
  width: 100%;
  height: 48px;
  border-radius: ${props => props.theme.borderRadius.large};
`;

export const InputContainer = styled.div`
  display: flex;
  gap: 5px;
  justify-content: flex-start;
  width: 100%;
`;

export const Prefix = styled.div`
  ${StyledTypo.uiTextCss};
  margin: 15px 0 0 20px;
  font-size: 16px;
  color: ${props => transparentize(0.4, props.theme.color[Color.Blue_1000_Deep])};
`;
